/**
 * @file useProductSkus.ts
 * @description Фасовки товара и действия с ними для «Инвентарь и Цены»
 * (phase19.13 — архив фасовок; вынесено из `ProductInventorySection` в
 * phase19.22, когда секция получила печать этикеток и перевалила за 200 строк).
 *
 * Фасовку со складской историей сервер не удаляет, а отправляет в архив: тост
 * берёт формулировку из ответа (`message`) — «удалена» и «перемещена в архив»
 * это разные новости для менеджера. Архивные — в конце списка.
 *
 * phase19.31: подтверждения — окном кабинета (`useConfirm`), а не
 * системным `confirm()`; быстрая правка цены и остатка полями в строке убрана —
 * они меняются в окне фасовки.
 */

import { useCallback, useEffect, useState } from "react";

import { useNotify } from "../../../../components/ui/NotifyProvider";
import { useConfirm } from "../../../../components/ui/use-confirm";
import {
  catalogSkus,
  catalogUnits,
  type CatalogSku,
  type CatalogUnit,
} from "../../../../lib/api-catalog";
import { ApiError } from "../../../../lib/api-client";
import { skuRestoreErrorText } from "../sku-restore-errors";

export function useProductSkus(productId: string) {
  const notify = useNotify();
  const confirmApi = useConfirm();
  const { confirm } = confirmApi;
  const [skus, setSkus] = useState<CatalogSku[]>([]);
  const [units, setUnits] = useState<CatalogUnit[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([catalogSkus.list(productId), catalogUnits.list()]);
      // Архивные — в конец списка: они справочные, работают с активными.
      setSkus([...s].sort((a, b) => Number(b.isActive) - Number(a.isActive)));
      setUnits(u);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось загрузить фасовки");
    } finally {
      setLoading(false);
    }
  }, [notify, productId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function remove(s: CatalogSku) {
    const ok = await confirm({
      title: `Удалить фасовку «${s.code}»?`,
      description:
        "Если по фасовке есть складские движения или заказы, она уйдёт в архив: с сайта снимется, история сохранится.",
      action: "Удалить",
      danger: true,
    });
    if (!ok) return;
    try {
      // Текст итога даёт сервер: удалили физически или отправили в архив —
      // решает он, админка это только пересказывает.
      const result = await catalogSkus.remove(productId, s.id);
      await refresh();
      if (result.archived) notify.info(result.message);
      else notify.success(result.message);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить");
    }
  }

  /** Возврат фасовки из архива: подтверждение → тост сервера → перечитать список. */
  async function restore(s: CatalogSku) {
    const ok = await confirm({
      title: `Вернуть фасовку «${s.code}» в оборот?`,
      description: "Фасовка снова станет доступна на сайте и в новых заказах.",
      action: "Вернуть",
    });
    if (!ok) return;
    try {
      const result = await catalogSkus.restore(productId, s.id);
      await refresh();
      notify.success(result.message);
    } catch (err) {
      notify.error(skuRestoreErrorText(err));
    }
  }

  return { skus, units, loading, refresh, remove, restore, confirmRequest: confirmApi.request, settleConfirm: confirmApi.settle };
}
